import React, { useState, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  Search,
  Plus,
  MoreVertical,
  Lightbulb,
  Check,
  Shield,
  Trash2,
  Edit2,
  Users,
  Building2,
  CreditCard,
  CheckCircle2,
  X,
  Lock,
  Phone,
  Mail,
  UserPlus,
  Upload,
  Camera,
  FileSignature,
  ChevronDown,
  UserCheck,
} from 'lucide-react';
import {
  AdminSystemUser,
  RolePermissionConfig,
  PermissionActionKey,
} from '../../../types';
import {
  INITIAL_SYSTEM_USERS,
  INITIAL_ROLE_PERMISSIONS,
  ESTATE_MGMT_PERMISSION_ROWS,
  ACCOUNTING_PERMISSION_ROWS,
  SECURITY_PERMISSION_ROWS,
  buildInitialEstatePermissions,
  buildInitialAccountingPermissions,
  buildInitialSecurityPermissions,
} from '../../../data/mockPermissionsData';

interface Props {
  onBack: () => void;
}

type ViewScreen =
  | 'user_list'
  | 'add_staff'
  | 'edit_staff'
  | 'user_permissions'
  | 'role_list'
  | 'role_edit';

type PermissionTab = 'estate_mgmt' | 'accounting' | 'security';

export const AdminUserPermissionsView: React.FC<Props> = ({ onBack }) => {
  // Navigation screen
  const [currentScreen, setCurrentScreen] = useState<ViewScreen>('user_list');

  // System Users State (Unlimited users)
  const [users, setUsers] = useState<AdminSystemUser[]>(INITIAL_SYSTEM_USERS);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenuUserId, setActiveMenuUserId] = useState<string | null>(null);

  // Selected User for Editing or Permission Setup
  const [selectedUser, setSelectedUser] = useState<AdminSystemUser | null>(null);

  // Roles & Permissions State
  const [roles, setRoles] = useState<RolePermissionConfig[]>(INITIAL_ROLE_PERMISSIONS);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  // Add / Edit Staff Form State (Image 1)
  const [staffUsername, setStaffUsername] = useState('silverman03');
  const [staffPassword, setStaffPassword] = useState('1234');
  const [staffFirstName, setStaffFirstName] = useState('silverman');
  const [staffLastName, setStaffLastName] = useState('Admin');
  const [staffPosition, setStaffPosition] = useState('admin');
  const [staffAvatar, setStaffAvatar] = useState<string>('');
  const [staffSignature, setStaffSignature] = useState<string>('');
  const [staffPhone, setStaffPhone] = useState('');
  const [staffEmail, setStaffEmail] = useState('');

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  // Active Role Editing State
  const [editingRoleName, setEditingRoleName] = useState('');
  const [activeTab, setActiveTab] = useState<PermissionTab>('estate_mgmt');

  // Selected Role in User Permission Setup Dropdown (Image 3)
  const [selectedRoleDropdown, setSelectedRoleDropdown] = useState<string>('ไม่ระบุค่า');

  // Current Permission Matrix State
  const [currentPermissions, setCurrentPermissions] = useState<{
    estate_mgmt: Record<string, Record<PermissionActionKey, boolean>>;
    accounting: Record<string, Record<PermissionActionKey, boolean>>;
    security: Record<string, Record<PermissionActionKey, boolean>>;
  }>({
    estate_mgmt: buildInitialEstatePermissions(false),
    accounting: buildInitialAccountingPermissions(false),
    security: buildInitialSecurityPermissions(false),
  });

  // Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Username validation regex: a-z, A-Z, 0-9, and hyphen/underscore
  const isUsernameValid = useMemo(() => {
    if (!staffUsername.trim()) return false;
    return /^[a-zA-Z0-9_-]+$/.test(staffUsername.trim());
  }, [staffUsername]);

  // Filtered Users for Search
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.full_name.toLowerCase().includes(q) ||
        u.position.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  // Group permission rows by category
  const groupedEstateRows = useMemo(() => {
    const map = new Map<string, typeof ESTATE_MGMT_PERMISSION_ROWS>();
    ESTATE_MGMT_PERMISSION_ROWS.forEach((r) => {
      if (!map.has(r.category)) {
        map.set(r.category, []);
      }
      map.get(r.category)!.push(r);
    });
    return Array.from(map.entries());
  }, []);

  const groupedAccountingRows = useMemo(() => {
    const map = new Map<string, typeof ACCOUNTING_PERMISSION_ROWS>();
    ACCOUNTING_PERMISSION_ROWS.forEach((r) => {
      if (!map.has(r.category)) {
        map.set(r.category, []);
      }
      map.get(r.category)!.push(r);
    });
    return Array.from(map.entries());
  }, []);

  const groupedSecurityRows = useMemo(() => {
    const map = new Map<string, typeof SECURITY_PERMISSION_ROWS>();
    SECURITY_PERMISSION_ROWS.forEach((r) => {
      if (!map.has(r.category)) {
        map.set(r.category, []);
      }
      map.get(r.category)!.push(r);
    });
    return Array.from(map.entries());
  }, []);

  // Handler: Open Add Staff Screen (Image 1)
  const handleOpenAddStaff = () => {
    setSelectedUser(null);
    setStaffUsername('silverman' + Math.floor(10 + Math.random() * 90));
    setStaffPassword('1234');
    setStaffFirstName('');
    setStaffLastName('');
    setStaffPosition('admin');
    setStaffAvatar('');
    setStaffSignature('');
    setStaffPhone('');
    setStaffEmail('');
    setActiveMenuUserId(null);
    setCurrentScreen('add_staff');
  };

  // Handler: Open Edit Staff Screen
  const handleOpenEditStaff = (user: AdminSystemUser) => {
    setSelectedUser(user);
    setStaffUsername(user.username);
    setStaffPassword(user.password || '••••');
    setStaffFirstName(user.first_name || user.full_name.split(' ')[0] || '');
    setStaffLastName(user.last_name || user.full_name.split(' ').slice(1).join(' ') || '');
    setStaffPosition(user.position);
    setStaffAvatar(user.avatar_url || '');
    setStaffSignature(user.signature_url || '');
    setStaffPhone(user.phone || '');
    setStaffEmail(user.email || '');
    setActiveMenuUserId(null);
    setCurrentScreen('edit_staff');
  };

  // Handler: Save Staff (Add or Edit)
  const handleSaveStaff = (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffUsername.trim()) {
      showToast('กรุณาระบุชื่อเข้าใช้งาน');
      return;
    }
    if (!isUsernameValid) {
      showToast('ชื่อเข้าใช้งานใส่ได้เฉพาะ a-z, 0-9 และตัวอักษรพิเศษ - หรือ _ เท่านั้น');
      return;
    }
    if (!staffFirstName.trim() && !staffLastName.trim()) {
      showToast('กรุณาระบุชื่อหรือนามสกุล');
      return;
    }

    const fullName = `${staffFirstName.trim()} ${staffLastName.trim()}`.trim();
    const now = new Date();
    const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(
      now.getMonth() + 1
    ).padStart(2, '0')}/${now.getFullYear()} - ${String(now.getHours()).padStart(
      2,
      '0'
    )}:${String(now.getMinutes()).padStart(2, '0')} น.`;

    if (selectedUser) {
      // Edit User
      setUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                first_name: staffFirstName.trim(),
                last_name: staffLastName.trim(),
                full_name: fullName,
                position: staffPosition.trim() || 'admin',
                username: staffUsername.trim(),
                password: staffPassword,
                avatar_url: staffAvatar,
                signature_url: staffSignature,
                phone: staffPhone,
                email: staffEmail,
              }
            : u
        )
      );
      showToast(`บันทึกข้อมูลเจ้าหน้าที่ "${fullName}" เรียบร้อยแล้ว`);
    } else {
      // Create New User
      const newUser: AdminSystemUser = {
        id: `user-${Date.now()}`,
        first_name: staffFirstName.trim(),
        last_name: staffLastName.trim(),
        full_name: fullName,
        position: staffPosition.trim() || 'admin',
        username: staffUsername.trim(),
        password: staffPassword,
        last_online: formattedDate,
        avatar_url: staffAvatar,
        signature_url: staffSignature,
        phone: staffPhone,
        email: staffEmail,
        status: 'active',
      };
      setUsers((prev) => [newUser, ...prev]);
      showToast(`เพิ่มเจ้าหน้าที่นิติบุคคล "${fullName}" สำเร็จ`);
    }

    setCurrentScreen('user_list');
  };

  // Handler: Delete User
  const handleDeleteUser = (userId: string, name: string) => {
    setActiveMenuUserId(null);
    if (confirm(`คุณต้องการลบผู้ใช้งาน "${name}" ออกจากระบบหรือไม่?`)) {
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      showToast(`ลบผู้ใช้งาน "${name}" เรียบร้อยแล้ว`);
    }
  };

  // Handler: Open User Permission Screen (Image 2 -> Image 3)
  const handleOpenUserPermissions = (user: AdminSystemUser) => {
    setSelectedUser(user);
    setActiveMenuUserId(null);

    // Check if user has a corresponding role
    const matchedRole = roles.find(
      (r) => r.role_name.toLowerCase() === user.position.toLowerCase()
    );

    if (user.custom_permissions) {
      setCurrentPermissions(JSON.parse(JSON.stringify(user.custom_permissions)));
      setSelectedRoleDropdown(matchedRole ? matchedRole.role_name : 'ไม่ระบุค่า');
    } else if (matchedRole) {
      setCurrentPermissions(JSON.parse(JSON.stringify(matchedRole.permissions)));
      setSelectedRoleDropdown(matchedRole.role_name);
    } else {
      // Default empty/unspecified permissions
      setCurrentPermissions({
        estate_mgmt: buildInitialEstatePermissions(false),
        accounting: buildInitialAccountingPermissions(false),
        security: buildInitialSecurityPermissions(false),
      });
      setSelectedRoleDropdown('ไม่ระบุค่า');
    }

    setActiveTab('estate_mgmt');
    setCurrentScreen('user_permissions');
  };

  // Handler: Dropdown Role Change in Image 3
  const handleRoleDropdownChange = (roleName: string) => {
    setSelectedRoleDropdown(roleName);
    if (roleName === 'ไม่ระบุค่า') {
      return;
    }
    const found = roles.find((r) => r.role_name === roleName);
    if (found) {
      setCurrentPermissions(JSON.parse(JSON.stringify(found.permissions)));
      showToast(`โหลดสิทธิ์ตามตำแหน่ง "${roleName}" แล้ว`);
    }
  };

  // Handler: Save User Permissions (Image 3)
  const handleSaveUserPermissions = () => {
    if (!selectedUser) return;

    setUsers((prev) =>
      prev.map((u) =>
        u.id === selectedUser.id
          ? {
              ...u,
              position:
                selectedRoleDropdown !== 'ไม่ระบุค่า'
                  ? selectedRoleDropdown
                  : u.position,
              custom_permissions: currentPermissions,
            }
          : u
      )
    );

    showToast(`บันทึกสิทธิ์การเข้าใช้งานของ "${selectedUser.full_name}" สำเร็จ`);
    setCurrentScreen('user_list');
  };

  // Handler: Open Role Edit (For role template management)
  const handleOpenRoleEdit = (role?: RolePermissionConfig) => {
    if (role) {
      setSelectedRoleId(role.id);
      setEditingRoleName(role.role_name);
      setCurrentPermissions(JSON.parse(JSON.stringify(role.permissions)));
    } else {
      setSelectedRoleId(null);
      setEditingRoleName('ตำแหน่งใหม่');
      setCurrentPermissions({
        estate_mgmt: buildInitialEstatePermissions(false),
        accounting: buildInitialAccountingPermissions(false),
        security: buildInitialSecurityPermissions(false),
      });
    }
    setActiveTab('estate_mgmt');
    setCurrentScreen('role_edit');
  };

  // Handler: Save Role
  const handleSaveRole = () => {
    if (!editingRoleName.trim()) {
      showToast('กรุณาระบุชื่อตำแหน่งงาน');
      return;
    }

    if (selectedRoleId) {
      setRoles((prev) =>
        prev.map((r) =>
          r.id === selectedRoleId
            ? { ...r, role_name: editingRoleName, permissions: currentPermissions }
            : r
        )
      );
      showToast(`บันทึกสิทธิ์ตำแหน่ง "${editingRoleName}" เรียบร้อยแล้ว`);
    } else {
      const newRole: RolePermissionConfig = {
        id: `role-${Date.now()}`,
        role_name: editingRoleName,
        description: `กำหนดสิทธิ์สำหรับตำแหน่ง ${editingRoleName}`,
        permissions: currentPermissions,
      };
      setRoles((prev) => [...prev, newRole]);
      showToast(`เพิ่มสิทธิ์ตำแหน่ง "${editingRoleName}" เรียบร้อยแล้ว`);
    }

    setCurrentScreen('role_list');
  };

  // Handler: Delete Role
  const handleDeleteRole = (roleId: string, roleName: string) => {
    if (confirm(`คุณต้องการลบตำแหน่ง "${roleName}" หรือไม่?`)) {
      setRoles((prev) => prev.filter((r) => r.id !== roleId));
      showToast(`ลบตำแหน่ง "${roleName}" เรียบร้อยแล้ว`);
    }
  };

  // Permission Checkbox Toggle Helper
  const handleToggleSingleAction = (
    tab: PermissionTab,
    rowId: string,
    action: PermissionActionKey
  ) => {
    setCurrentPermissions((prev) => {
      const tabObj = { ...prev[tab] };
      const currentVal = tabObj[rowId]?.[action] ?? false;
      tabObj[rowId] = {
        ...tabObj[rowId],
        [action]: !currentVal,
      };
      return {
        ...prev,
        [tab]: tabObj,
      };
    });
  };

  // Toggle "All" for a single row
  const handleToggleRowAll = (tab: PermissionTab, rowId: string) => {
    setCurrentPermissions((prev) => {
      const tabObj = { ...prev[tab] };
      const row = tabObj[rowId] || {
        view: false,
        create: false,
        edit: false,
        approve: false,
        print: false,
        cancel: false,
      };
      const isAllChecked =
        row.view && row.create && row.edit && row.approve && row.print && row.cancel;
      const targetVal = !isAllChecked;

      tabObj[rowId] = {
        view: targetVal,
        create: targetVal,
        edit: targetVal,
        approve: targetVal,
        print: targetVal,
        cancel: targetVal,
      };

      return {
        ...prev,
        [tab]: tabObj,
      };
    });
  };

  // File Upload Handlers (Avatar & Signature)
  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('ขนาดไฟล์เกิน 10 MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setStaffAvatar(reader.result as string);
        showToast('อัปโหลดรูปภาพสำเร็จ');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('ขนาดไฟล์เกิน 10 MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setStaffSignature(reader.result as string);
        showToast('อัปโหลดลายเซ็นต์สำเร็จ');
      };
      reader.readAsDataURL(file);
    }
  };

  // Preset Role Names for Image 3 Dropdown
  const roleDropdownOptions = useMemo(() => {
    const defaultList = [
      'ไม่ระบุค่า',
      'PM',
      'ทีม 1',
      'บัญชี',
      'เทส',
      'ss',
      'ac',
      'หัวหน้างาน',
      'BA',
      'บัญชีลูกหนี้',
      'เจ้าหน้าที่บัญชี',
    ];
    // Add any roles from state that aren't already listed
    roles.forEach((r) => {
      if (!defaultList.includes(r.role_name)) {
        defaultList.push(r.role_name);
      }
    });
    return defaultList;
  }, [roles]);

  return (
    <div id="admin-user-permissions-module" className="space-y-6 max-w-5xl mx-auto font-prompt">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-50 p-4 bg-slate-900 text-white rounded-2xl shadow-xl flex items-center gap-3 animate-in fade-in zoom-in-95 text-xs font-semibold border border-slate-700">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={avatarInputRef}
        onChange={handleAvatarFileChange}
        accept="image/jpeg,image/png,image/jpg"
        className="hidden"
      />
      <input
        type="file"
        ref={signatureInputRef}
        onChange={handleSignatureFileChange}
        accept="image/jpeg,image/png,image/jpg"
        className="hidden"
      />

      {/* ========================================================================= */}
      {/* SCREEN 1 (IMAGE 2): ตั้งค่าสิทธิ์ผู้ใช้งาน (User List with 3-dot dropdown) */}
      {/* ========================================================================= */}
      {currentScreen === 'user_list' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Top Bar matching Image 2 */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
                title="ย้อนกลับไปเมนูตั้งค่า"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                ตั้งค่าสิทธิ์ผู้ใช้งาน
              </h1>
            </div>

            {/* Action Buttons on Right matching Image 2 */}
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setCurrentScreen('role_list')}
                className="px-4 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>เพิ่มสิทธิ์ของตำแหน่งงาน</span>
              </button>

              <button
                type="button"
                onClick={handleOpenAddStaff}
                className="px-4 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>เพิ่มเจ้าหน้าที่</span>
              </button>
            </div>
          </div>

          {/* Search Box on Right matching Image 2 */}
          <div className="flex justify-end pt-1">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="ค้นหา"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-10 py-2 bg-white border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 placeholder:text-slate-400"
              />
              <Search className="w-4 h-4 text-slate-400 absolute right-3 top-2.5" />
            </div>
          </div>

          {/* User Table matching Image 2 */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-visible">
            <div className="overflow-x-auto min-h-[350px]">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-100/90 text-slate-600 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3.5 px-6 font-semibold text-slate-700">ชื่อ - นามสกุล</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-700">ตำแหน่ง</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-700">ID</th>
                    <th className="py-3.5 px-4 font-semibold text-slate-700">ออนไลน์ล่าสุด</th>
                    <th className="py-3.5 px-4 text-center w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-50/70 transition-colors">
                      {/* Name + Avatar */}
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-3">
                          {user.avatar_url ? (
                            <img
                              src={user.avatar_url}
                              alt={user.full_name}
                              referrerPolicy="no-referrer"
                              className="w-8 h-8 rounded-full object-cover border border-slate-200"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                              <Users className="w-4 h-4" />
                            </div>
                          )}
                          <span className="font-medium text-slate-800">{user.full_name}</span>
                        </div>
                      </td>

                      {/* Position */}
                      <td className="py-3 px-4 text-slate-700 font-normal">{user.position}</td>

                      {/* ID / Username */}
                      <td className="py-3 px-4 font-mono text-slate-600">{user.username}</td>

                      {/* Last Online */}
                      <td className="py-3 px-4 font-mono text-slate-500">{user.last_online}</td>

                      {/* Action Menu (3 dots) matching Image 2 */}
                      <td className="py-3 px-4 text-center relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuUserId(
                              activeMenuUserId === user.id ? null : user.id
                            );
                          }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {/* Dropdown Menu Popup matching Image 2 */}
                        {activeMenuUserId === user.id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setActiveMenuUserId(null)}
                            />
                            <div className="absolute right-4 top-10 z-50 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 min-w-[170px] text-left text-xs font-medium text-slate-700 animate-in fade-in zoom-in-95">
                              {/* 1. ตั้งค่าสิทธิ์การเข้าใช้งาน (Image 2 item) */}
                              <button
                                type="button"
                                onClick={() => handleOpenUserPermissions(user)}
                                className="w-full text-left px-3.5 py-2 hover:bg-blue-50 hover:text-blue-700 text-slate-700 flex items-center gap-2 cursor-pointer"
                              >
                                <span>ตั้งค่าสิทธิ์การเข้าใช้งาน</span>
                              </button>

                              {/* 2. แก้ไข (Image 2 item) */}
                              <button
                                type="button"
                                onClick={() => handleOpenEditStaff(user)}
                                className="w-full text-left px-3.5 py-2 hover:bg-blue-50 hover:text-blue-700 text-slate-700 flex items-center gap-2 cursor-pointer"
                              >
                                <span>แก้ไข</span>
                              </button>

                              {/* 3. ลบ (Image 2 item) */}
                              <button
                                type="button"
                                onClick={() => handleDeleteUser(user.id, user.full_name)}
                                className="w-full text-left px-3.5 py-2 hover:bg-rose-50 hover:text-rose-600 text-slate-700 flex items-center gap-2 cursor-pointer border-t border-slate-100"
                              >
                                <span>ลบ</span>
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}

                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-400 text-xs">
                        ไม่พบข้อมูลผู้ใช้งานที่ค้นหา
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 2 (IMAGE 1): เพิ่มเจ้าหน้าที่นิติบุคคล / แก้ไขข้อมูลเจ้าหน้าที่ */}
      {/* ========================================================================= */}
      {(currentScreen === 'add_staff' || currentScreen === 'edit_staff') && (
        <div className="space-y-4 animate-in fade-in duration-200">
          {/* Header matching Image 1 */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCurrentScreen('user_list')}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              {currentScreen === 'add_staff'
                ? 'เพิ่มเจ้าหน้าที่นิติบุคคล'
                : 'แก้ไขข้อมูลเจ้าหน้าที่นิติบุคคล'}
            </h1>
          </div>

          {/* Main Card Container matching Image 1 */}
          <form
            onSubmit={handleSaveStaff}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-12">
              {/* Left Column: รูปภาพ & ลายเซ็นต์ */}
              <div className="md:col-span-5 space-y-8 border-b md:border-b-0 md:border-r border-slate-100 pb-6 md:pb-0 md:pr-8">
                {/* 1. Avatar Upload */}
                <div className="flex items-start gap-4">
                  <div className="relative shrink-0">
                    {staffAvatar ? (
                      <img
                        src={staffAvatar}
                        alt="Avatar Preview"
                        className="w-20 h-20 rounded-full object-cover border-2 border-slate-200 shadow-inner"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-slate-200 flex items-center justify-center text-slate-300">
                        <Users className="w-10 h-10" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 text-xs">
                    <h3 className="font-bold text-slate-800 text-sm">รูปภาพ</h3>
                    <p className="text-[11px] text-rose-500 font-medium">
                      แนบไฟล์ .jpg ขนาดไม่เกิน 10 MB
                    </p>
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-xs pt-1 block cursor-pointer hover:underline"
                    >
                      เปลี่ยนรูปภาพ
                    </button>
                  </div>
                </div>

                {/* 2. Signature Upload */}
                <div className="flex items-start gap-4 pt-2">
                  <div className="relative shrink-0">
                    {staffSignature ? (
                      <img
                        src={staffSignature}
                        alt="Signature Preview"
                        className="w-20 h-20 rounded-2xl object-contain bg-slate-50 border-2 border-slate-200 p-1"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-slate-100 border-2 border-slate-200 flex items-center justify-center text-slate-300">
                        <FileSignature className="w-9 h-9" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-1 text-xs">
                    <h3 className="font-bold text-slate-800 text-sm">ลายเซ็นต์</h3>
                    <p className="text-[11px] text-rose-500 font-medium">
                      แนบไฟล์ .jpg ขนาดไม่เกิน 10 MB
                    </p>
                    <p className="text-[11px] text-rose-500 font-medium">
                      ขนาดภาพแนะนำ สูง 180 px กว้าง 400 px
                    </p>
                    <button
                      type="button"
                      onClick={() => signatureInputRef.current?.click()}
                      className="text-blue-600 hover:text-blue-800 font-semibold text-xs pt-1 block cursor-pointer hover:underline"
                    >
                      เปลี่ยนรูปภาพ
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column: Form Fields matching Image 1 */}
              <div className="md:col-span-7 space-y-4 text-xs">
                {/* ชื่อเข้าใช้งาน (Username) */}
                <div className="space-y-1">
                  <label className="font-bold text-slate-800 text-xs block">
                    ชื่อเข้าใช้งาน
                  </label>
                  <p className="text-[11px] text-rose-500 leading-tight">
                    ใส่ได้เฉพาะ a-z ทั้งพิมพ์ใหญ่และพิมพ์เล็ก ตัวเลข 0-9 และตัวอักษรพิเศษ - เท่านั้น
                  </p>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={staffUsername}
                      onChange={(e) => setStaffUsername(e.target.value)}
                      placeholder="silverman03"
                      className={`w-full px-3.5 py-2.5 rounded-lg border text-xs font-mono font-medium text-slate-800 outline-none transition-all ${
                        isUsernameValid
                          ? 'border-emerald-500 bg-white focus:ring-2 focus:ring-emerald-500/20'
                          : 'border-slate-300 bg-slate-50 focus:border-blue-500 focus:bg-white'
                      }`}
                    />
                    {isUsernameValid && (
                      <Check className="w-4 h-4 text-emerald-600 absolute right-3 top-3" />
                    )}
                  </div>
                </div>

                {/* รหัสผ่าน (Password) */}
                <div className="space-y-1">
                  <label className="font-bold text-slate-800 text-xs block">
                    รหัสผ่าน
                  </label>
                  <input
                    type="text"
                    required
                    value={staffPassword}
                    onChange={(e) => setStaffPassword(e.target.value)}
                    placeholder="1234"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-xs font-mono text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>

                {/* ชื่อ (First Name) */}
                <div className="space-y-1">
                  <label className="font-bold text-slate-800 text-xs block">
                    ชื่อ
                  </label>
                  <input
                    type="text"
                    required
                    value={staffFirstName}
                    onChange={(e) => setStaffFirstName(e.target.value)}
                    placeholder="silverman"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 bg-blue-50/40 text-xs font-medium text-slate-800 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>

                {/* นามสกุล (Last Name) */}
                <div className="space-y-1">
                  <label className="font-bold text-slate-800 text-xs block">
                    นามสกุล
                  </label>
                  <input
                    type="text"
                    value={staffLastName}
                    onChange={(e) => setStaffLastName(e.target.value)}
                    placeholder="Admin"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-200 bg-blue-50/40 text-xs font-medium text-slate-800 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>

                {/* ตำแหน่ง (Position) */}
                <div className="space-y-1">
                  <label className="font-bold text-slate-800 text-xs block">
                    ตำแหน่ง
                  </label>
                  <input
                    type="text"
                    required
                    value={staffPosition}
                    onChange={(e) => setStaffPosition(e.target.value)}
                    placeholder="admin"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-xs font-medium text-slate-800 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none"
                  />
                </div>

                {/* Action Buttons matching Image 1 */}
                <div className="flex items-center gap-3 pt-6">
                  <button
                    type="submit"
                    className="flex-1 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-lg text-xs font-semibold shadow-sm transition-colors cursor-pointer text-center"
                  >
                    ยืนยัน
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentScreen('user_list')}
                    className="flex-1 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer text-center"
                  >
                    ยกเลิก
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 3 (IMAGE 3): ตั้งค่าสิทธิ์การเข้าใช้งาน (User Permission Matrix) */}
      {/* ========================================================================= */}
      {currentScreen === 'user_permissions' && (
        <div className="space-y-5 animate-in fade-in duration-200">
          {/* Header Bar matching Image 3 */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCurrentScreen('user_list')}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                ตั้งค่าสิทธิ์การเข้าใช้งาน
              </h1>
              <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-400/20 shrink-0" />
            </div>
          </div>

          {/* User & Position Selector Box matching Image 3 */}
          <div className="bg-white p-5 sm:p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-5">
            {selectedUser && (
              <div className="flex items-center justify-between p-3.5 bg-blue-50/60 rounded-xl border border-blue-100 text-xs">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                    {selectedUser.full_name.charAt(0)}
                  </div>
                  <div>
                    <span className="font-bold text-slate-800 block text-sm">
                      {selectedUser.full_name}
                    </span>
                    <span className="text-slate-500 font-mono text-[11px]">
                      ID: {selectedUser.username} | ตำแหน่งปัจจุบัน: {selectedUser.position}
                    </span>
                  </div>
                </div>
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-semibold rounded-lg text-[11px]">
                  สถานะ: ใช้งาน
                </span>
              </div>
            )}

            {/* Dropdown Position Selector matching Image 3 */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 max-w-xl">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 shrink-0">
                <span>ตำแหน่งงาน</span>
                <Lightbulb className="w-4 h-4 text-amber-500 fill-amber-400/20" />
              </label>

              <div className="relative flex-1">
                <select
                  value={selectedRoleDropdown}
                  onChange={(e) => handleRoleDropdownChange(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 rounded-lg text-xs font-semibold text-slate-800 appearance-none outline-none transition-all pr-10 cursor-pointer"
                >
                  {roleDropdownOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-3 pointer-events-none" />
              </div>
            </div>

            {/* 3 Main Category Tabs matching Image 3 */}
            <div className="border-b border-slate-200 flex items-center gap-8 pt-2 overflow-x-auto">
              <button
                type="button"
                onClick={() => setActiveTab('estate_mgmt')}
                className={`pb-3 text-xs font-semibold transition-all relative whitespace-nowrap cursor-pointer ${
                  activeTab === 'estate_mgmt'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                การจัดการทะเบียนลูกบ้าน
                {activeTab === 'estate_mgmt' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('accounting')}
                className={`pb-3 text-xs font-semibold transition-all relative whitespace-nowrap cursor-pointer ${
                  activeTab === 'accounting'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                บัญชีและการเงิน
                {activeTab === 'accounting' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('security')}
                className={`pb-3 text-xs font-semibold transition-all relative whitespace-nowrap cursor-pointer ${
                  activeTab === 'security'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                ซิลเวอร์แมน การ์ด
                {activeTab === 'security' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>
            </div>
          </div>

          {/* Permissions Matrix Table matching Image 3 */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              {/* TAB 1: การจัดการทะเบียนลูกบ้าน */}
              {activeTab === 'estate_mgmt' && (
                <div className="divide-y divide-slate-100">
                  {groupedEstateRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      {/* Section Category Header */}
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      {/* Section Rows */}
                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.estate_mgmt[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                {/* ทั้งหมด */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('estate_mgmt', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* ดู */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* สร้าง */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* แก้ไข */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* อนุมัติ */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* พิมพ์ */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                {/* ยกเลิก */}
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* TAB 2: บัญชีและการเงิน */}
              {activeTab === 'accounting' && (
                <div className="divide-y divide-slate-100">
                  {groupedAccountingRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.accounting[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('accounting', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* TAB 3: ซิลเวอร์แมน การ์ด */}
              {activeTab === 'security' && (
                <div className="divide-y divide-slate-100">
                  {groupedSecurityRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.security[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('security', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Save Button Bar */}
          <div className="flex justify-end pt-2 pb-6">
            <button
              type="button"
              onClick={handleSaveUserPermissions}
              className="px-8 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-900/20 transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              <span>บันทึก</span>
            </button>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 4: รายการตำแหน่งงาน (Role List) */}
      {/* ========================================================================= */}
      {currentScreen === 'role_list' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentScreen('user_list')}
                className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
                title="ย้อนกลับ"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                ตั้งค่าสิทธิ์ของตำแหน่งงาน
              </h1>
            </div>

            <button
              type="button"
              onClick={() => handleOpenRoleEdit()}
              className="px-4 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer self-start sm:self-auto"
            >
              <Plus className="w-4 h-4" />
              <span>เพิ่มสิทธิ์ของตำแหน่งงาน</span>
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-100/90 text-slate-600 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="py-3.5 px-6 font-semibold text-slate-700">ตำแหน่ง</th>
                    <th className="py-3.5 px-6 text-right font-semibold text-slate-700">การจัดการ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {roles.map((role) => (
                    <tr
                      key={role.id}
                      onClick={() => handleOpenRoleEdit(role)}
                      className="hover:bg-blue-50/60 transition-colors cursor-pointer group"
                    >
                      <td className="py-3.5 px-6 font-medium text-slate-800 group-hover:text-blue-800">
                        {role.role_name}
                      </td>
                      <td className="py-3.5 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenRoleEdit(role)}
                            className="px-2.5 py-1 text-[11px] text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg font-medium border border-blue-200 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <Edit2 className="w-3 h-3" /> แก้ไขสิทธิ์
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteRole(role.id, role.role_name)}
                            className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="ลบตำแหน่ง"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* SCREEN 5: กำหนดสิทธิ์ตำแหน่งงาน (Role Edit Template) */}
      {/* ========================================================================= */}
      {currentScreen === 'role_edit' && (
        <div className="space-y-5 animate-in fade-in duration-200">
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCurrentScreen('role_list')}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors shadow-2xs cursor-pointer"
              title="ย้อนกลับ"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
                ตั้งค่าสิทธิ์ของตำแหน่งงาน
              </h1>
              <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-400/20 shrink-0" />
            </div>
          </div>

          <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 max-w-xl">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 shrink-0">
                <span>เพิ่มตำแหน่งงาน</span>
                <Lightbulb className="w-4 h-4 text-amber-500 fill-amber-400/20" />
              </label>
              <input
                type="text"
                value={editingRoleName}
                onChange={(e) => setEditingRoleName(e.target.value)}
                placeholder="ระบุชื่อตำแหน่งงาน เช่น เจ้าหน้าที่บัญชี, PM, หัวหน้างาน"
                className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-300 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 rounded-xl text-xs font-medium text-slate-800 outline-none transition-all"
              />
            </div>

            <div className="border-b border-slate-200 flex items-center gap-8 pt-2">
              <button
                type="button"
                onClick={() => setActiveTab('estate_mgmt')}
                className={`pb-3 text-xs font-semibold transition-all relative cursor-pointer ${
                  activeTab === 'estate_mgmt'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                การจัดการทะเบียนลูกบ้าน
                {activeTab === 'estate_mgmt' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('accounting')}
                className={`pb-3 text-xs font-semibold transition-all relative cursor-pointer ${
                  activeTab === 'accounting'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                บัญชีและการเงิน
                {activeTab === 'accounting' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('security')}
                className={`pb-3 text-xs font-semibold transition-all relative cursor-pointer ${
                  activeTab === 'security'
                    ? 'text-blue-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                ซิลเวอร์แมน การ์ด
                {activeTab === 'security' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              {activeTab === 'estate_mgmt' && (
                <div className="divide-y divide-slate-100">
                  {groupedEstateRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.estate_mgmt[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('estate_mgmt', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('estate_mgmt', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'accounting' && (
                <div className="divide-y divide-slate-100">
                  {groupedAccountingRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.accounting[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('accounting', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('accounting', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'security' && (
                <div className="divide-y divide-slate-100">
                  {groupedSecurityRows.map(([categoryName, rows]) => (
                    <div key={categoryName} className="border-b border-slate-200 last:border-b-0">
                      <div className="bg-slate-200/80 px-6 py-3 flex items-center justify-between font-bold text-slate-800 text-xs">
                        <span className="tracking-wide">{categoryName}</span>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4 text-center text-slate-700 text-xs font-bold w-[340px] sm:w-[460px]">
                          <span>ทั้งหมด</span>
                          <span>ดู</span>
                          <span>สร้าง</span>
                          <span>แก้ไข</span>
                          <span>อนุมัติ</span>
                          <span>พิมพ์</span>
                          <span>ยกเลิก</span>
                        </div>
                      </div>

                      <div className="divide-y divide-slate-100">
                        {rows.map((row) => {
                          const rowPerms = currentPermissions.security[row.id] || {
                            view: false,
                            create: false,
                            edit: false,
                            approve: false,
                            print: false,
                            cancel: false,
                          };
                          const isAllChecked =
                            rowPerms.view &&
                            rowPerms.create &&
                            rowPerms.edit &&
                            rowPerms.approve &&
                            rowPerms.print &&
                            rowPerms.cancel;

                          return (
                            <div
                              key={row.id}
                              className="px-6 py-3 flex items-center justify-between hover:bg-slate-50/80 transition-colors text-xs"
                            >
                              <span className="text-slate-700 font-normal pr-4">{row.name}</span>

                              <div className="grid grid-cols-7 gap-2 sm:gap-4 items-center text-center w-[340px] sm:w-[460px] shrink-0">
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={isAllChecked}
                                    onChange={() =>
                                      handleToggleRowAll('security', row.id)
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.view}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'view')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.create}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'create')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.edit}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'edit')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.approve}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'approve')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.print}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'print')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                                <div className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={rowPerms.cancel}
                                    onChange={() =>
                                      handleToggleSingleAction('security', row.id, 'cancel')
                                    }
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-2 pb-6">
            <button
              type="button"
              onClick={handleSaveRole}
              className="px-8 py-2.5 bg-[#0e52a0] hover:bg-[#0b4282] text-white rounded-xl text-xs font-semibold shadow-md shadow-blue-900/20 transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" />
              <span>บันทึก</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
